// Tiny bridge that lets panels (directions, location) command the map.
// MapContainer registers its fitBounds implementation on mount; panels call
// it without needing a direct reference to the map instance.

type FitBoundsFn = (
  bounds: [[number, number], [number, number]],
  options?: { padding?: number | { top: number; bottom: number; left: number; right: number }; maxZoom?: number }
) => void;

let fitBoundsImpl: FitBoundsFn | null = null;

export function registerFitBounds(fn: FitBoundsFn): () => void {
  fitBoundsImpl = fn;
  return () => {
    if (fitBoundsImpl === fn) fitBoundsImpl = null;
  };
}

/** Frame the given [west, south] / [east, north] bounds on screen. */
export function fitBounds(
  bounds: [[number, number], [number, number]],
  options?: { padding?: number | { top: number; bottom: number; left: number; right: number }; maxZoom?: number }
): boolean {
  if (!fitBoundsImpl) return false;
  fitBoundsImpl(bounds, options);
  return true;
}

export function hasMap(): boolean {
  return fitBoundsImpl !== null;
}

/**
 * Build a bounding box that contains every [lng, lat] pair given.
 */
export function bboxOf(
  coords: [number, number][]
): [[number, number], [number, number]] | null {
  if (!coords.length) return null;

  let minLng = 180;
  let maxLng = -180;
  let minLat = 90;
  let maxLat = -90;

  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  // Guard against zero-area bounds (single point routes)
  const EPS = 0.002;
  if (maxLng - minLng < EPS) {
    minLng -= EPS;
    maxLng += EPS;
  }
  if (maxLat - minLat < EPS) {
    minLat -= EPS;
    maxLat += EPS;
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}